import debounce from 'lodash/debounce';
import { useEffect, useRef, useState } from 'react';
import { StatusAdornmentStatus } from '../../../components/StatusAdornment';
import LangEnum from '../../../lang/LangEnum';
import { countries } from '../../../lib/countries';
import { getAuthLoginPath } from '../../../routes/auth.routes';
import snackbarStore from '../../../stores/Snackbar.store';
import { useAuthContext } from '../../login/AuthContext';
import { useTranslation } from '../../translation/TranslationContext';
import { useSignUpContext } from '../SignUp.context';
import { CnpjStatus, SignUpSteps } from '../types/signUp.types';

const DEBOUNCE_FIELD_TIME = 1000;
const CNPJ_DIGITS_LENGTH = 14;
const CNPJ_STATUS_SNACKBAR_ID = 'cnpj-status';

const useLocationAndCnpjStep = () => {
  const {
    state,
    handleChangeLocation,
    handleChangeCnpj,
    handleResetCnpjStatus,
    handleChangeStep,
    validateCnpj,
  } = useSignUpContext();
  const { location, cnpj, cnpjStatus, errorMessage } = state;
  const { loginContext } = useAuthContext();
  const { translate } = useTranslation();

  const debouncedValidate = useRef(debounce(validateCnpj, DEBOUNCE_FIELD_TIME)).current;

  const [isCnpjRequiredError, setIsCnpjRequiredError] = useState(false);

  const isBr = location?.code === 'BR';
  const loginPath = getAuthLoginPath(loginContext?.slug);

  const countryOptions = countries.map((country) => ({
    value: country.code,
    label: `${country.label} (${country.code})`,
  }));

  const handleCnpjChange = (value: string) => {
    setIsCnpjRequiredError(false);
    handleChangeCnpj(value);
    const isComplete = value.replace(/\D/g, '').length === CNPJ_DIGITS_LENGTH;
    if (isComplete) {
      debouncedValidate(value);
    } else {
      debouncedValidate.cancel();
      handleResetCnpjStatus();
    }
  };

  const handleCountryChange = (code: string) => {
    const country = countries.find((item) => item.code === code);
    if (country) {
      handleChangeLocation(country);
    }
  };

  const canProceedFromCnpj = async () => {
    if (!isBr) {
      return true;
    }
    if (!cnpj) {
      setIsCnpjRequiredError(true);
      return false;
    }
    if (cnpjStatus === 'success') {
      return true;
    }
    debouncedValidate.cancel();
    return validateCnpj(cnpj);
  };

  const handleContinue = async () => {
    if (await canProceedFromCnpj()) {
      handleChangeStep(SignUpSteps.STORE);
    }
  };

  const isInvalid = cnpjStatus === 'error';
  const isCnpjAlreadyRegistered = cnpjStatus === 'cnpjAlreadyExists';
  const hasFieldError = isInvalid || isCnpjAlreadyRegistered || isCnpjRequiredError;

  const getCnpjHelperTextKey = (): LangEnum | undefined => {
    if (isCnpjRequiredError) {
      return LangEnum.GLOBAL_REQUIRED;
    }
    if (isInvalid) {
      return LangEnum.GLOBAL_INVALID_CNPJ;
    }
    return undefined;
  };

  const cnpjHelperTextKey = getCnpjHelperTextKey();

  useEffect(() => {
    if (!isInvalid && !isCnpjAlreadyRegistered) {
      snackbarStore.close(CNPJ_STATUS_SNACKBAR_ID);
      return;
    }

    snackbarStore.handleMessage({
      id: CNPJ_STATUS_SNACKBAR_ID,
      type: 'error',
      title: isInvalid ? translate(LangEnum.GLOBAL_INVALID_CNPJ) : undefined,
      message: isInvalid ? translate(LangEnum.LOGIN_RECOVER_ERROR_HINT) : errorMessage,
    });
  }, [isInvalid, isCnpjAlreadyRegistered, errorMessage, translate]);

  useEffect(
    () => () => {
      snackbarStore.close(CNPJ_STATUS_SNACKBAR_ID);
    },
    [],
  );

  const cnpjStatusMap: Record<CnpjStatus, StatusAdornmentStatus> = {
    cnpjAlreadyExists: 'error',
    error: 'error',
    success: 'success',
    loading: 'loading',
    unset: 'unset',
  };
  const cnpjAdornmentStatus = cnpjStatusMap[cnpjStatus];

  return {
    location,
    cnpj,
    cnpjStatus,
    isBr,
    loginPath,
    countryOptions,
    handleCnpjChange,
    handleCountryChange,
    handleContinue,
    errorMessage,
    isInvalid,
    isCnpjAlreadyRegistered,
    hasFieldError,
    cnpjHelperTextKey,
    cnpjAdornmentStatus,
  };
};

export default useLocationAndCnpjStep;
